import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import { useSocialExportConnectedForStation } from '@app/modules/social-export/hooks/useSocialExportConnectedForStation';
import { JobStatus } from '@mi-front/core';
import { Info } from '@mi-front/ui';
import { Alert, Box } from '@mui/material';
import AlertTitle from '@mui/material/AlertTitle';
import { useIntl } from 'react-intl';
import { useFragmentCtx } from '../../../context/fragment-ctx';
import { AudiogramSocialExportStart } from './AudiogramSocialExportStart';

export function AudiogramSocialExportPlatform({ platform }: { platform: SocialPlatform }) {
  const { $t } = useIntl();
  const fragment = useFragmentCtx();
  const isConnected = useSocialExportConnectedForStation(fragment.stationId, platform);
  const status = fragment.audiogramExport?.platforms?.[platform.id];

  if (!isConnected) {
    return null;
  }

  const renderStatus = () => {
    if (!status) {
      return (
        <Alert severity="warning">
          <AlertTitle>
            {$t({
              id: 'audiogram.videoNotSent',
              defaultMessage: 'Video no enviado',
            })}
          </AlertTitle>
          <Box mb={1}>
            <AudiogramSocialExportStart fragment={fragment} platform={platform} />
          </Box>
        </Alert>
      );
    }

    if (status.status === JobStatus.COMPLETED) {
      return (
        <Alert severity="success">
          <AlertTitle>
            {$t({
              id: 'audiogram.exportSuccess',
              defaultMessage: 'Audiograma exportado correctamente',
            })}
          </AlertTitle>
          <Box mb={2}>
            {$t({
              id: 'common.url',
              defaultMessage: 'URL:',
            })}{' '}
            <a href={status.result?.videoUrl} target="_blank" rel="noreferrer">
              {status.result?.videoUrl}
            </a>
          </Box>
        </Alert>
      );
    }

    if (status.status === JobStatus.IN_PROGRESS || status.status === JobStatus.QUEUED) {
      return (
        <Alert severity="info">
          <AlertTitle>
            {$t({
              id: 'audiogram.exportInProgress',
              defaultMessage: 'Export en curso...',
            })}
          </AlertTitle>
        </Alert>
      );
    }

    if (status.status === JobStatus.FAILED) {
      return (
        <Alert severity="error">
          <AlertTitle>
            {$t({
              id: 'audiogram.exportFailed',
              defaultMessage: 'Export fallido',
            })}
          </AlertTitle>
          <Box mb={2}>
            {$t({ id: 'common.error', defaultMessage: 'Error' })}: <i>{status.failedReason}</i>
          </Box>
          <Box mb={1}>
            <AudiogramSocialExportStart fragment={fragment} platform={platform} />
          </Box>
        </Alert>
      );
    }

    return null;
  };

  return (
    <Box mb={3}>
      <Info
        title={
          <div>
            {platform.Icon()}{' '}
            {$t(
              {
                id: 'audiogram.exportToPlatformTitle',
                defaultMessage: '{platformName} Export',
              },
              { platformName: platform.name },
            )}
          </div>
        }
        data={renderStatus()}
      />
    </Box>
  );
}
